import FrameProvider from './FrameProvider';

import {
  MAX_EXTRACTION_RESOLUTION,
  SECOND_IN_MILLISECONDS,
} from '../../../constants';

import { info } from '../../../util/log';
import performance from '../../../util/performance';

import CanvasImageBitmapInterface from '../CanvasImageBitmapInterface';
import CanvasElementWrapper from '../../../util/videoExtraction/elementWrappers/CanvasElementWrapper';
import VideoElementWrapper from '../../../util/videoExtraction/elementWrappers/VideoElementWrapper';
import VideoFile from '../models/VideoFile';
import { getMediaFileUrl } from '../../../api/creativeSuiteApi';
export default class HTML5FrameProvider extends FrameProvider {
  _logId = null;

  /**
   * @function fetchFrames
   */
  fetchFrames = async (frameProviderPrioritizationInterface) => {
    const {
      unavailableFrameRanges,
      videoFile,
    } = frameProviderPrioritizationInterface;
    if (!VideoFile.isVideoFile(videoFile)) return;

    const { mediaIdentifier } = videoFile;
    this._logId = mediaIdentifier;
    this.initialize(mediaIdentifier);

    try {
      const videoWrapper = await this._loadVideo(videoFile);
      this._log('Download complete. Starting extraction.');

      const totalExtractionStartTime = performance.now();
      let totalNumberOfFrames = 0;
      for (let i = 0; i < unavailableFrameRanges.length; i++) {
        const frameRange = unavailableFrameRanges[i];
        totalNumberOfFrames += frameRange.end - frameRange.start + 1;
        await this._fetchPlayableFrameRange(
          mediaIdentifier,
          frameRange,
          videoWrapper,
        );
      }

      this.extractionCompleted(mediaIdentifier);

      const totalExtractionTime = this._getExtractionTime(
        totalExtractionStartTime,
      );
      const totalExtractionFPS = Math.round(
        totalNumberOfFrames / totalExtractionTime,
      );
      info(
        `Video (${mediaIdentifier}) took ${totalExtractionTime}s to extract averaging at ${totalExtractionFPS}xFPS`,
      );
    } catch (error) {
      this._log(error);
    }
  };

  _loadVideo = async (videoFile) => {
    if (!VideoFile.isVideoFile(videoFile)) return null;

    const { serverId, url } = videoFile;
    let videoUrl;
    let useCors = true;
    try {
      videoUrl = await getMediaFileUrl(serverId).then((result) => {
        if (result.success) {
          return result.data.uri;
        }
        throw new Error('Could not get media file URL');
      });
    } catch (error) {
      this._log(`Could not use CORS-friendly URL, using default URL: ${error}`);
      videoUrl = url;
      useCors = false;
    }

    const videoWrapper = new VideoElementWrapper(videoUrl);

    this._log('Started downloading');
    const loadAndBufferPromise = videoWrapper.loadAndBuffer(useCors);

    try {
      const isLoadedAndBuffered = await loadAndBufferPromise;
      if (!isLoadedAndBuffered) {
        throw new Error('Failed to download.');
      }
    } catch (error) {
      throw new Error('Failed to load and buffer video');
    }

    return videoWrapper;
  };

  _fetchPlayableFrameRange = async (
    mediaIdentifier,
    playableFrameRange,
    videoWrapper,
  ) => {
    const { end: frameEnd, start: frameStart } = playableFrameRange;

    const getLogMessage = (message) => {
      return `[${frameStart} : ${frameEnd}]: ${message}`;
    };

    const extractionStartTime = performance.now();

    const totalNumberOfFrames = playableFrameRange.length;
    const scale = this._getCompressedResolutionScale(videoWrapper);

    // Performance logging stuff.
    let hasExtractedAnything = false;
    let lastProgressLogged = undefined;

    for (let i = frameStart; i <= frameEnd; i++) {
      if (
        hasExtractedAnything &&
        !this.shouldContinueExtraction(mediaIdentifier)
      ) {
        this._log(getLogMessage('Being removed. Canceling extraction!'));
        return;
      }

      const progress = Math.round(
        (100 * (totalNumberOfFrames - (frameEnd - i))) / totalNumberOfFrames,
      );
      if (
        hasExtractedAnything &&
        lastProgressLogged !== progress &&
        progress % 10 === 0
      ) {
        this._log(getLogMessage(`Extracting frame ${i} ${progress}%`));
        lastProgressLogged = progress;
      }

      await videoWrapper.goToFrame(i);
      const canvasWrapper = new CanvasElementWrapper();
      canvasWrapper.setPixels(videoWrapper, scale);

      this.notifyFrameReadyListeners(
        new CanvasImageBitmapInterface(
          mediaIdentifier,
          i,
          canvasWrapper.getElement(),
        ),
      );
      hasExtractedAnything = true;
    }

    if (hasExtractedAnything) {
      const durationSeconds = this._getExtractionTime(extractionStartTime);
      const extractionFPS = Math.round(totalNumberOfFrames / durationSeconds);
      // FIXME(caskew): This is a mean, but naming singularly as it'll be surfaced as a single value in GA. Reconsider?
      this._log(
        getLogMessage(
          `Done with extraction in ${durationSeconds}s @ ${extractionFPS}xFPS.`,
        ),
      );
    }
  };

  _log = (message) => {
    info(`Video (${this._logId})\n${message}`);
  };

  _getCompressedResolutionScale = (videoWrapper) => {
    return this._getScaleFromDimensions(videoWrapper.getDimensions());
  };

  _getExtractionTime = (extractionStartTime) => {
    const durationMs = performance.now() - extractionStartTime;
    return Math.round(durationMs) / SECOND_IN_MILLISECONDS;
  };

  _getScaleFromDimensions = (dimensions) => {
    const { width, height } = dimensions;
    return Math.min(1, MAX_EXTRACTION_RESOLUTION / Math.min(width, height));
  };
}
